import React, { useState, useEffect } from "react";
import Layout from "../../components/Layout/Layout";
import { Card, Tabs, Button, Spin, message } from 'antd';
import { FaHospital, FaTags, FaCalendarAlt } from 'react-icons/fa';
import BeatLoader from "react-spinners/BeatLoader";
import { toast } from "react-toastify";
import { getFacilityWithTagAvailabilities } from "../../services/facilityTagAvailabilityService";
import FacilityTagAvailability from "./FacilityTagAvailability/FacilityTagAvailability"; // Import the new component
import { useParams } from "react-router-dom";
import './facility-details.css';

const { TabPane } = Tabs;

function FacilityDetailsPage() {
  // Get facility ID from URL parameters
  const { id: facilityId } = useParams();

  // State management
  const [facilityData, setFacilityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");
  
  // Fetch facility data with tag availabilities
  const fetchFacilityData = async () => {
    setLoading(true);
    
    try {
      const response = await getFacilityWithTagAvailabilities(facilityId);
      
      if (response?.data?.success) {
        setFacilityData(response.data.data);
      } else {
        toast.error("Failed to fetch facility data");
      }
    } catch (error) {
      console.error("Error fetching facility data:", error);
      toast.error(error.message || "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('PROPS: ', facilityId);
  },[facilityId])
  
  // Initial data fetch
  useEffect(() => {
    fetchFacilityData();
  }, [facilityId]);
  
  if (loading) {
    return (
      <Layout title="Facility Details" pageTitle="Facility Details">
        <div className="loading-container">
          <BeatLoader color="#322659" />
          <p className="loading-message">Loading facility data...</p>
        </div>
      </Layout>
    );
  }
  
  if (!facilityData) {
    return (
      <Layout title="Facility Details" pageTitle="Facility Details">
        <div>Facility not found</div>
      </Layout>
    );
  }
  
  // Format the address correctly based on its structure
  const formatAddress = (address) => {
    // If address is a string, return it directly
    if (typeof address === 'string') {
      return address;
    }
    
    // If address is an object, format it
    if (typeof address === 'object' && address !== null) {
      const { street, city, area, postalCode } = address;
      const parts = [];
      
      if (street) parts.push(street);
      if (area) parts.push(area);
      if (city) parts.push(city);
      if (postalCode) parts.push(postalCode);
      
      return parts.join(', ');
    }
    
    // Fallback
    return 'Address not available';
  };
  
  return (
    <Layout title={facilityData.facilityName} pageTitle={facilityData.facilityName}>
      <div className="section">
        {/* Facility Overview Card */}
        <Card 
          className="facility-overview-card"
          title={
            <div className="card-title">
              <FaHospital style={{ marginRight: 8 }} />
              <span>Facility Overview</span>
            </div>
          }
        >
          <div className="facility-overview-content">
            <div className="facility-basic-info">
              <h3>{facilityData.facilityName}</h3>
              <p className="facility-type">{facilityData.facilityType}</p>
              <p className="facility-address">{formatAddress(facilityData.address)}</p>
            </div>
            
            <div className="facility-contacts">
              <p><strong>Phone:</strong> {facilityData.phone1}</p>
              {facilityData.phone2 && <p><strong>Alternative Phone:</strong> {facilityData.phone2}</p>}
              <p><strong>Email:</strong> {facilityData.email}</p>
            </div>
          </div>
        </Card>
        
        {/* Main Tabs */}
        <div className="tab-container">
          <Tabs 
            activeKey={activeTab} 
            onChange={setActiveTab}
            type="card"
          >
            {/* Tag Availability Tab */}
            <TabPane 
              tab={
                <span>
                  <FaCalendarAlt style={{ marginRight: 8 }} />
                  Tag Availability
                </span>
              } 
              key="1"
            >
              <FacilityTagAvailability 
                facilityId={facilityId}
                facilityTags={facilityData.facilityTags || []}
                refreshData={fetchFacilityData}
              />
            </TabPane>
            
            {/* Services Tab */}
            <TabPane 
              tab={
                <span>
                  <FaTags style={{ marginRight: 8 }} />
                  Services
                </span>
              } 
              key="2"
            >
              <Card className="services-card">
                <h3>Available Services</h3>
                <div className="services-list">
                  {facilityData.services && facilityData.services.map((service, index) => (
                    <div key={index} className="service-item">
                      {service}
                    </div>
                  ))}
                </div>
              </Card>
            </TabPane>
            
            {/* More tabs as needed */}
          </Tabs>
        </div>
      </div>
    </Layout>
  );
}

export default FacilityDetailsPage;